/**
 * TaBiFoTo Admin API Adapters
 * ─────────────────────────────────────────────────────────────
 * Converts between backend response shapes and frontend store types.
 *
 * CMS resources (services, portfolio, homepage sections):
 *   Backend: JSONB { title_i18n: { zh, en, ja } }
 *   Frontend: flat { title_zh, title_en, title_ja }
 *
 * Operations resources (photographers, availability, orders, confirmations):
 *   Backend: flat fields — no JSONB multilingual
 *
 * Legal documents:
 *   Backend: JSONB { title_i18n, content_i18n }
 *   Frontend: flat { title_zh, title_en, title_ja, content_zh, ... }
 */

import type {
  Service, ServiceOption, PortfolioItem, HomepageSection,
  Photographer, PhotographerAvailability, PhotographerStatus, AvailabilityStatus,
  Order, OrderStatus, PaymentStatus,
  BookingConfirmation, ConfirmationStatus,
  LegalDocument, LegalDocType, LegalI18nLang,
  BookingCostItem, CostType, CostStatus,
  BookingNote, NoteType,
  OrderCancellationLog,
  OrderRescheduleLog,
  Itinerary, ItineraryStatus,
} from "../store/types";

// ─── I18n JSONB helpers ───────────────────────────────────────────────────────

interface I18nField { zh?: string; en?: string; ja?: string; }

function resolveI18n(
  jsonb: I18nField | undefined,
  flatZh: string | undefined,
  flatEn: string | undefined,
  flatJa: string | undefined
) {
  return {
    zh: jsonb?.zh ?? flatZh ?? "",
    en: jsonb?.en ?? flatEn ?? "",
    ja: jsonb?.ja ?? flatJa ?? "",
  };
}

const NOW = () => new Date().toISOString();

// ─── Service adapters ─────────────────────────────────────────────────────────

interface ApiService {
  id: string; service_code?: string;
  title_i18n?: I18nField; title_zh?: string; title_en?: string; title_ja?: string;
  short_desc_i18n?: I18nField; short_desc_zh?: string; short_desc_en?: string; short_desc_ja?: string;
  features?: Array<{ zh?: string; en?: string; ja?: string }>;
  base_price_twd?: number; duration_hours?: number; city_scope?: string[];
  cover_image_url?: string; icon?: string; is_active?: boolean; sort_order?: number;
  created_at?: string; updated_at?: string;
}

export function adaptServiceFromApi(raw: unknown): Service {
  const r = raw as ApiService;
  const title = resolveI18n(r.title_i18n, r.title_zh, r.title_en, r.title_ja);
  const desc = resolveI18n(r.short_desc_i18n, r.short_desc_zh, r.short_desc_en, r.short_desc_ja);
  return {
    id: r.id, service_code: r.service_code ?? "",
    title_zh: title.zh, title_en: title.en, title_ja: title.ja,
    short_desc_zh: desc.zh, short_desc_en: desc.en, short_desc_ja: desc.ja,
    base_price_twd: r.base_price_twd ?? 0, duration_hours: r.duration_hours ?? 8,
    city_scope: r.city_scope ?? [], cover_image_url: r.cover_image_url ?? "",
    icon: r.icon ?? "ri-camera-line",
    features: (r.features ?? []).map((f) => ({ zh: f.zh ?? "", en: f.en ?? "", ja: f.ja ?? "" })),
    is_active: r.is_active ?? true, sort_order: r.sort_order ?? 99,
    created_at: r.created_at ?? NOW(), updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptServiceToApi(svc: Service): Record<string, unknown> {
  return {
    service_code: svc.service_code,
    title_i18n: { zh: svc.title_zh, en: svc.title_en, ja: svc.title_ja },
    short_desc_i18n: { zh: svc.short_desc_zh, en: svc.short_desc_en, ja: svc.short_desc_ja },
    base_price_twd: svc.base_price_twd, duration_hours: svc.duration_hours,
    city_scope: svc.city_scope, cover_image_url: svc.cover_image_url,
    icon: svc.icon, features: svc.features, is_active: svc.is_active, sort_order: svc.sort_order,
  };
}

// ─── Service Option adapters ──────────────────────────────────────────────────

interface ApiServiceOption {
  id: string; service_id?: string; option_code?: string;
  title_i18n?: I18nField; title_zh?: string; title_en?: string; title_ja?: string;
  price_delta_twd?: number; is_active?: boolean; sort_order?: number;
}

export function adaptServiceOptionFromApi(raw: unknown): ServiceOption {
  const r = raw as ApiServiceOption;
  const title = resolveI18n(r.title_i18n, r.title_zh, r.title_en, r.title_ja);
  return {
    id: r.id, service_id: r.service_id ?? "", option_code: r.option_code ?? "",
    title_zh: title.zh, title_en: title.en, title_ja: title.ja,
    price_delta_twd: r.price_delta_twd ?? 0, is_active: r.is_active ?? true, sort_order: r.sort_order ?? 99,
  };
}

export function adaptServiceOptionToApi(opt: ServiceOption): Record<string, unknown> {
  return {
    service_id: opt.service_id, option_code: opt.option_code,
    title_i18n: { zh: opt.title_zh, en: opt.title_en, ja: opt.title_ja },
    price_delta_twd: opt.price_delta_twd, is_active: opt.is_active, sort_order: opt.sort_order,
  };
}

// ─── Portfolio Item adapters ──────────────────────────────────────────────────

interface ApiPortfolioItem {
  id: string; title?: string; city?: string; shoot_type?: string; media_type?: string;
  thumbnail_url?: string; media_url?: string; cover_image_url?: string;
  description_i18n?: I18nField; description_zh?: string; description_en?: string; description_ja?: string;
  photographer_id?: string; is_published?: boolean; is_featured?: boolean; is_showreel?: boolean;
  homepage_display_mode?: string; sort_order?: number; created_at?: string; updated_at?: string;
}

export function adaptPortfolioFromApi(raw: unknown): PortfolioItem {
  const r = raw as ApiPortfolioItem;
  const desc = resolveI18n(r.description_i18n, r.description_zh, r.description_en, r.description_ja);
  return {
    id: r.id, title: r.title ?? "", city: r.city ?? "", shoot_type: r.shoot_type ?? "portrait",
    media_type: (r.media_type as "image" | "video") ?? "image",
    thumbnail_url: r.thumbnail_url ?? "", media_url: r.media_url ?? "", cover_image_url: r.cover_image_url ?? "",
    description_zh: desc.zh, description_en: desc.en, description_ja: desc.ja,
    photographer_id: r.photographer_id ?? "", is_published: r.is_published ?? false,
    is_featured: r.is_featured ?? false, is_showreel: r.is_showreel ?? false,
    homepage_display_mode: (r.homepage_display_mode as "reel" | "still" | "both") ?? "still",
    sort_order: r.sort_order ?? 99, created_at: r.created_at ?? NOW(), updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptPortfolioToApi(item: PortfolioItem): Record<string, unknown> {
  return {
    title: item.title, city: item.city, shoot_type: item.shoot_type, media_type: item.media_type,
    thumbnail_url: item.thumbnail_url, media_url: item.media_url, cover_image_url: item.cover_image_url,
    description_i18n: { zh: item.description_zh, en: item.description_en, ja: item.description_ja },
    photographer_id: item.photographer_id, is_published: item.is_published,
    is_featured: item.is_featured, is_showreel: item.is_showreel,
    homepage_display_mode: item.homepage_display_mode, sort_order: item.sort_order,
  };
}

// ─── Homepage Section adapters ────────────────────────────────────────────────

interface ApiHomepageSection {
  id: string; section_key?: string;
  title_i18n?: I18nField; title_zh?: string; title_en?: string; title_ja?: string;
  content_i18n?: I18nField; content_zh?: string; content_en?: string; content_ja?: string;
  image_url?: string; is_active?: boolean; sort_order?: number;
}

export function adaptSectionFromApi(raw: unknown): HomepageSection {
  const r = raw as ApiHomepageSection;
  const title = resolveI18n(r.title_i18n, r.title_zh, r.title_en, r.title_ja);
  const content = resolveI18n(r.content_i18n, r.content_zh, r.content_en, r.content_ja);
  return {
    id: r.id, section_key: r.section_key ?? "hero",
    title_zh: title.zh, title_en: title.en, title_ja: title.ja,
    content_zh: content.zh, content_en: content.en, content_ja: content.ja,
    image_url: r.image_url ?? "", is_active: r.is_active ?? true, sort_order: r.sort_order ?? 99,
  };
}

export function adaptSectionToApi(section: HomepageSection): Record<string, unknown> {
  return {
    section_key: section.section_key,
    title_i18n: { zh: section.title_zh, en: section.title_en, ja: section.title_ja },
    content_i18n: { zh: section.content_zh, en: section.content_en, ja: section.content_ja },
    image_url: section.image_url, is_active: section.is_active, sort_order: section.sort_order,
  };
}

// ─── Photographer adapters — flat schema, no JSONB ────────────────────────────

interface ApiPhotographer {
  id: string; photographer_code?: string; display_name?: string; legal_name?: string;
  email?: string; phone?: string; country_code?: string; city_base?: string;
  languages?: string[]; status?: string; portfolio_url?: string; notes?: string;
  is_active?: boolean; created_at?: string; updated_at?: string;
}

export function adaptPhotographerFromApi(raw: unknown): Photographer {
  const r = raw as ApiPhotographer;
  return {
    id: r.id,
    photographer_code: r.photographer_code ?? "",
    display_name: r.display_name ?? "",
    legal_name: r.legal_name ?? "",
    email: r.email ?? "",
    phone: r.phone ?? "",
    country_code: r.country_code ?? "JP",
    city_base: r.city_base ?? "",
    languages: r.languages ?? [],
    status: (r.status as PhotographerStatus) ?? "active",
    portfolio_url: r.portfolio_url ?? "",
    notes: r.notes ?? "",
    is_active: r.is_active ?? true,
    created_at: r.created_at ?? NOW(),
    updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptPhotographerToApi(ph: Photographer): Record<string, unknown> {
  return {
    photographer_code: ph.photographer_code,
    display_name: ph.display_name,
    legal_name: ph.legal_name,
    email: ph.email,
    phone: ph.phone,
    country_code: ph.country_code,
    city_base: ph.city_base,
    languages: ph.languages,
    status: ph.status,
    portfolio_url: ph.portfolio_url,
    notes: ph.notes,
    is_active: ph.is_active,
  };
}

// ─── Photographer Availability adapters ──────────────────────────────────────

interface ApiAvailability {
  id: string; photographer_id?: string; available_date?: string;
  available_start_time?: string; available_end_time?: string;
  city?: string; status?: string; note?: string;
  created_at?: string; updated_at?: string;
}

export function adaptAvailabilityFromApi(raw: unknown): PhotographerAvailability {
  const r = raw as ApiAvailability;
  return {
    id: r.id,
    photographer_id: r.photographer_id ?? "",
    available_date: r.available_date ?? "",
    available_start_time: r.available_start_time ?? "09:00",
    available_end_time: r.available_end_time ?? "18:00",
    city: r.city ?? "",
    status: (r.status as AvailabilityStatus) ?? "available",
    note: r.note ?? "",
    created_at: r.created_at ?? NOW(),
    updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptAvailabilityToApi(a: PhotographerAvailability): Record<string, unknown> {
  return {
    photographer_id: a.photographer_id,
    available_date: a.available_date,
    available_start_time: a.available_start_time,
    available_end_time: a.available_end_time,
    city: a.city,
    status: a.status,
    note: a.note,
  };
}

// ─── Order adapters ───────────────────────────────────────────────────────────

interface ApiOrder {
  id: string; order_number?: string; customer_name?: string; customer_email?: string;
  customer_phone?: string; service_id?: string; selected_city?: string;
  shoot_date?: string; start_time?: string; end_time?: string; number_of_reels?: number;
  subtotal_twd?: number; addons_total_twd?: number; total_twd?: number; currency?: string;
  order_status?: string; payment_status?: string; special_requests?: string;
  internal_notes?: string; created_at?: string; updated_at?: string;
}

export function adaptOrderFromApi(raw: unknown): Order {
  const r = raw as ApiOrder;
  return {
    id: r.id, order_number: r.order_number ?? "",
    customer_name: r.customer_name ?? "", customer_email: r.customer_email ?? "",
    customer_phone: r.customer_phone ?? "", service_id: r.service_id ?? "",
    selected_city: r.selected_city ?? "", shoot_date: r.shoot_date ?? "",
    start_time: r.start_time ?? "", end_time: r.end_time ?? "",
    number_of_reels: r.number_of_reels ?? 0,
    subtotal_twd: r.subtotal_twd ?? 0, addons_total_twd: r.addons_total_twd ?? 0,
    total_twd: r.total_twd ?? 0, currency: r.currency ?? "TWD",
    order_status: (r.order_status as OrderStatus) ?? "pending",
    payment_status: (r.payment_status as PaymentStatus) ?? "unpaid",
    special_requests: r.special_requests ?? "", internal_notes: r.internal_notes ?? "",
    created_at: r.created_at ?? NOW(), updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptOrderToApi(o: Order): Record<string, unknown> {
  return {
    order_number: o.order_number, customer_name: o.customer_name,
    customer_email: o.customer_email, customer_phone: o.customer_phone,
    service_id: o.service_id, selected_city: o.selected_city,
    shoot_date: o.shoot_date, start_time: o.start_time, end_time: o.end_time,
    number_of_reels: o.number_of_reels,
    subtotal_twd: o.subtotal_twd, addons_total_twd: o.addons_total_twd, total_twd: o.total_twd,
    currency: o.currency, order_status: o.order_status, payment_status: o.payment_status,
    special_requests: o.special_requests, internal_notes: o.internal_notes,
  };
}

// ─── Booking Confirmation adapters ────────────────────────────────────────────

interface ApiBookingConfirmation {
  id: string; order_id?: string; photographer_id?: string;
  confirmation_status?: string; confirmed_at?: string | null; declined_at?: string | null;
  replacement_required?: boolean; note?: string; created_at?: string; updated_at?: string;
}

export function adaptConfirmationFromApi(raw: unknown): BookingConfirmation {
  const r = raw as ApiBookingConfirmation;
  return {
    id: r.id,
    order_id: r.order_id ?? "",
    photographer_id: r.photographer_id ?? "",
    confirmation_status: (r.confirmation_status as ConfirmationStatus) ?? "pending",
    confirmed_at: r.confirmed_at ?? null,
    declined_at: r.declined_at ?? null,
    replacement_required: r.replacement_required ?? false,
    note: r.note ?? "",
    created_at: r.created_at ?? NOW(),
    updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptConfirmationToApi(c: BookingConfirmation): Record<string, unknown> {
  return {
    order_id: c.order_id,
    photographer_id: c.photographer_id,
    confirmation_status: c.confirmation_status,
    confirmed_at: c.confirmed_at,
    declined_at: c.declined_at,
    replacement_required: c.replacement_required,
    note: c.note,
  };
}

// ─── Legal Document adapters ──────────────────────────────────────────────────

interface ApiLegalDocument {
  id: string;
  doc_type?: string;
  doc_key?: string;
  // New shape: { "zh-TW": "...", "en-US": "...", "ja-JP": "..." }
  title_i18n?: Record<string, string>;
  // Legacy fallback flat fields
  title_zh?: string; title_en?: string; title_ja?: string;
  content_i18n?: Record<string, string>;
  content_zh?: string; content_en?: string; content_ja?: string;
  version?: string; effective_date?: string; is_active?: boolean;
  audience?: string[];
  required_for?: string[];
  is_mandatory?: boolean;
  display_order?: number;
  created_at?: string; updated_at?: string;
}

function emptyI18n(): Record<LegalI18nLang, string> {
  return { "zh-TW": "", "en-US": "", "ja-JP": "" };
}

function resolveI18nJson(
  jsonb: Record<string, string> | undefined,
  flatZh: string | undefined,
  flatEn: string | undefined,
  flatJa: string | undefined
): Record<LegalI18nLang, string> {
  if (jsonb) {
    // Support both new keys (zh-TW/en-US/ja-JP) and legacy keys (zh/en/ja)
    return {
      "zh-TW": jsonb["zh-TW"] ?? jsonb["zh"] ?? flatZh ?? "",
      "en-US": jsonb["en-US"] ?? jsonb["en"] ?? flatEn ?? "",
      "ja-JP": jsonb["ja-JP"] ?? jsonb["ja"] ?? flatJa ?? "",
    };
  }
  return {
    "zh-TW": flatZh ?? "",
    "en-US": flatEn ?? "",
    "ja-JP": flatJa ?? "",
  };
}

export function adaptLegalDocFromApi(raw: unknown): LegalDocument {
  const r = raw as ApiLegalDocument;
  return {
    id: r.id,
    doc_type: (r.doc_type as LegalDocType) ?? "terms",
    doc_key: r.doc_key ?? "",
    title_i18n: resolveI18nJson(r.title_i18n, r.title_zh, r.title_en, r.title_ja),
    content_i18n: resolveI18nJson(r.content_i18n, r.content_zh, r.content_en, r.content_ja),
    version: r.version ?? "1.0",
    effective_date: r.effective_date ?? "",
    is_active: r.is_active ?? true,
    audience: r.audience ?? [],
    required_for: r.required_for ?? [],
    is_mandatory: r.is_mandatory ?? true,
    display_order: r.display_order ?? 99,
    created_at: r.created_at ?? NOW(),
    updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptLegalDocToApi(doc: LegalDocument): Record<string, unknown> {
  const safeTitle: Record<LegalI18nLang, string> = {
    "zh-TW": doc.title_i18n?.["zh-TW"] ?? "",
    "en-US": doc.title_i18n?.["en-US"] ?? "",
    "ja-JP": doc.title_i18n?.["ja-JP"] ?? "",
  };
  const safeContent: Record<LegalI18nLang, string> = {
    "zh-TW": doc.content_i18n?.["zh-TW"] ?? "",
    "en-US": doc.content_i18n?.["en-US"] ?? "",
    "ja-JP": doc.content_i18n?.["ja-JP"] ?? "",
  };
  return {
    doc_type: doc.doc_type,
    doc_key: doc.doc_key || null,
    title_i18n: safeTitle,
    content_i18n: safeContent,
    version: doc.version,
    effective_date: doc.effective_date,
    is_active: doc.is_active,
    audience: doc.audience ?? [],
    required_for: doc.required_for ?? [],
    is_mandatory: doc.is_mandatory ?? true,
    display_order: doc.display_order ?? 99,
  };
}

// ─── Booking Cost Item adapters ───────────────────────────────────────────────

interface ApiBookingCostItem {
  id: string; order_id?: string; cost_type?: string;
  amount_twd?: number; currency?: string; cost_status?: string;
  description?: string; created_at?: string; updated_at?: string;
}

export function adaptCostItemFromApi(raw: unknown): BookingCostItem {
  const r = raw as ApiBookingCostItem;
  return {
    id: r.id,
    order_id: r.order_id ?? "",
    cost_type: (r.cost_type as CostType) ?? "other",
    amount_twd: r.amount_twd ?? 0,
    currency: r.currency ?? "TWD",
    cost_status: (r.cost_status as CostStatus) ?? "estimated",
    description: r.description ?? "",
    created_at: r.created_at ?? NOW(),
    updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptCostItemToApi(c: BookingCostItem): Record<string, unknown> {
  return {
    order_id: c.order_id,
    cost_type: c.cost_type,
    amount_twd: c.amount_twd,
    currency: c.currency,
    cost_status: c.cost_status,
    description: c.description,
  };
}

// ─── Booking Note adapters ────────────────────────────────────────────────────

interface ApiBookingNote {
  id: string; order_id?: string; note_type?: string;
  content?: string; created_by?: string; created_at?: string;
}

export function adaptNoteFromApi(raw: unknown): BookingNote {
  const r = raw as ApiBookingNote;
  return {
    id: r.id,
    order_id: r.order_id ?? "",
    note_type: (r.note_type as NoteType) ?? "internal",
    content: r.content ?? "",
    created_by: r.created_by ?? "",
    created_at: r.created_at ?? NOW(),
  };
}

export function adaptNoteToApi(n: BookingNote): Record<string, unknown> {
  return {
    order_id: n.order_id,
    note_type: n.note_type,
    content: n.content,
    created_by: n.created_by,
  };
}

// ─── Order Cancellation Log adapters ─────────────────────────────────────────

interface ApiCancellationLog {
  id: string; order_id?: string; cancelled_at?: string; cancelled_by?: string;
  reason?: string; refund_amount_twd?: number; penalty_amount_twd?: number;
  non_refundable_cost_twd?: number; note?: string;
}

export function adaptCancellationLogFromApi(raw: unknown): OrderCancellationLog {
  const r = raw as ApiCancellationLog;
  return {
    id: r.id,
    order_id: r.order_id ?? "",
    cancelled_at: r.cancelled_at ?? NOW(),
    cancelled_by: r.cancelled_by ?? "",
    reason: r.reason ?? "",
    refund_amount_twd: r.refund_amount_twd ?? 0,
    penalty_amount_twd: r.penalty_amount_twd ?? 0,
    non_refundable_cost_twd: r.non_refundable_cost_twd ?? 0,
    note: r.note ?? "",
  };
}

export function adaptCancellationLogToApi(l: OrderCancellationLog): Record<string, unknown> {
  return {
    order_id: l.order_id,
    cancelled_at: l.cancelled_at,
    cancelled_by: l.cancelled_by,
    reason: l.reason,
    refund_amount_twd: l.refund_amount_twd,
    penalty_amount_twd: l.penalty_amount_twd,
    non_refundable_cost_twd: l.non_refundable_cost_twd,
    note: l.note,
  };
}

// ─── Order Reschedule Log adapters ────────────────────────────────────────────

interface ApiRescheduleLog {
  id: string; order_id?: string; original_shoot_date?: string; new_shoot_date?: string;
  requested_at?: string; approved_at?: string | null; reason?: string;
  reschedule_count?: number; note?: string;
}

export function adaptRescheduleLogFromApi(raw: unknown): OrderRescheduleLog {
  const r = raw as ApiRescheduleLog;
  return {
    id: r.id,
    order_id: r.order_id ?? "",
    original_shoot_date: r.original_shoot_date ?? "",
    new_shoot_date: r.new_shoot_date ?? "",
    requested_at: r.requested_at ?? NOW(),
    approved_at: r.approved_at ?? null,
    reason: r.reason ?? "",
    reschedule_count: r.reschedule_count ?? 1,
    note: r.note ?? "",
  };
}

export function adaptRescheduleLogToApi(l: OrderRescheduleLog): Record<string, unknown> {
  return {
    order_id: l.order_id,
    original_shoot_date: l.original_shoot_date,
    new_shoot_date: l.new_shoot_date,
    requested_at: l.requested_at,
    approved_at: l.approved_at,
    reason: l.reason,
    reschedule_count: l.reschedule_count,
    note: l.note,
  };
}

// ─── Itinerary adapters ───────────────────────────────────────────────────────

interface ApiItinerary {
  id: string;
  order_id?: string;
  photographer_id?: string | null;
  meeting_point?: string;
  meeting_time?: string | null;
  map_url?: string;
  notes?: string;
  internal_notes?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export function adaptItineraryFromApi(raw: unknown): Itinerary {
  const r = raw as ApiItinerary;
  return {
    id: r.id,
    order_id: r.order_id ?? "",
    photographer_id: r.photographer_id ?? null,
    meeting_point: r.meeting_point ?? "",
    meeting_time: r.meeting_time ?? null,
    map_url: r.map_url ?? "",
    notes: r.notes ?? "",
    internal_notes: r.internal_notes ?? "",
    status: (r.status as ItineraryStatus) ?? "draft",
    created_at: r.created_at ?? NOW(),
    updated_at: r.updated_at ?? NOW(),
  };
}

export function adaptItineraryToApi(it: Itinerary): Record<string, unknown> {
  return {
    order_id: it.order_id,
    photographer_id: it.photographer_id || null,
    meeting_point: it.meeting_point,
    meeting_time: it.meeting_time || null,
    map_url: it.map_url,
    notes: it.notes,
    internal_notes: it.internal_notes,
    status: it.status,
  };
}
